import React, {useState} from 'react';
import {Box, Typography, Accordion, AccordionSummary, Button} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SamplesSummaryTable, {SamplesSummaryTableRow} from '../SamplesSummaryTable';

const FEReview: React.FC<any> = ({formData, handleNext, samples}) => {
    const userSetFEAnalysisConfigurations = [
        {
            category: 'Samples',
            parameters: samples
                .filter((s: any) => formData.sample_ids.includes(s.id))
                .map((s: any) => {
                    return {
                        sample_name: s.name,
                        factor: s.other_categories.Factor ?? '',
                        tissue: s.other_categories.Tissue ?? '',
                        condition: s.other_categories.Condition ?? '',
                        replicate: s.other_categories.Replicate ?? '',
                        treatment: s.other_categories.Treatment ?? '',
                        sample_type: s.other_categories.SampleType ?? '',
                        control_name: s.other_categories.ControlName ?? ''
                    };
                })
        },

        {
            category: 'Settings',
            parameters: [
                {
                    name: 'Parameters for Functional Analysis',
                    values: [
                        {
                            name: 'Ontology',
                            value: formData.functional_enrichment_analysis.ontology
                        },
                        {
                            name: 'P Adjust Method',
                            value: formData.functional_enrichment_analysis.p_adjust_method
                        },
                        {
                            name: 'Q Value Cutoff',
                            value: formData.functional_enrichment_analysis.q_value_cutoff
                        }
                    ]
                }
            ]
        }
    ];

    const [listStates, setListStates] = useState(new Array(userSetFEAnalysisConfigurations.length).fill({open: true, editMode: false}));

    const sampleHeaders = ['Sample Name', 'Factor', 'Condition', 'Replicate', 'Treatment', 'Sample Type', 'Control Name'];
    const sampleRows: SamplesSummaryTableRow[] = userSetFEAnalysisConfigurations[0].parameters.map((param: any) => ({
        key: param.sample_name,
        cells: [param.sample_name, param.factor, param.condition, param.replicate, param.treatment, param.sample_type, param.control_name]
    }));

    return (
        <Box
            display="flex"
            flexDirection="column"
            gap="36px"
            marginBottom="24px"
            bgcolor={'surface-container-lowest'}
            p={3}
            borderRadius={'12px'}>
            <Box borderRadius="20px" border="1px solid" borderColor="outline-variant" overflow="hidden">
                <Accordion
                    expanded={listStates[0].open}
                    sx={{
                        backgroundColor: 'unset',
                        boxShadow: 'unset',
                        '& .MuiAccordionSummary-root, .MuiAccordionSummary-root.Mui-expanded': {
                            minHeight: '0'
                        },
                        '& .MuiAccordionSummary-root.Mui-expanded': {
                            borderBottom: '1px solid',
                            borderColor: 'outline-variant'
                        }
                    }}>
                    <AccordionSummary
                        aria-controls="panel1d-content"
                        expandIcon={<ArrowDropUpIcon />}
                        id="panel1d-header"
                        onClick={() =>
                            setListStates(state =>
                                state.map((item, index) => {
                                    if (index === 0) {
                                        return {...item, open: !item.open};
                                    }
                                    return item;
                                })
                            )
                        }
                        sx={{
                            backgroundColor: 'surface-container-high',
                            padding: '4px 4px 4px 16px',
                            height: '0',
                            minHeight: '0',
                            gap: '12px',
                            flexDirection: 'row-reverse',
                            alignItems: 'center',
                            '& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded': {
                                alignItems: 'center',
                                margin: 0
                            },
                            '& .MuiAccordionSummary-root.Mui-expanded': {}
                        }}>
                        <Typography variant="title" size="medium" width="100%">
                            {userSetFEAnalysisConfigurations[0].category}
                        </Typography>
                        <Button
                            startIcon={<EditOutlinedIcon />}
                            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                e.stopPropagation();
                                handleNext(1);
                            }}>
                            Edit
                        </Button>
                    </AccordionSummary>
                    <SamplesSummaryTable headers={sampleHeaders} rows={sampleRows} />
                </Accordion>
            </Box>

            <Box borderRadius="20px" border="1px solid" borderColor="outline-variant" overflow="hidden">
                <Accordion
                    expanded={listStates[1].open}
                    sx={{
                        backgroundColor: 'unset',
                        boxShadow: 'unset',
                        '& .MuiAccordionSummary-root, .MuiAccordionSummary-root.Mui-expanded': {
                            minHeight: '0'
                        },
                        '& .MuiAccordionSummary-root.Mui-expanded': {
                            borderBottom: '1px solid',
                            borderColor: 'outline-variant'
                        }
                    }}>
                    <AccordionSummary
                        aria-controls="panel1d-content"
                        expandIcon={<ArrowDropUpIcon />}
                        id="panel1d-header"
                        onClick={() =>
                            setListStates(state =>
                                state.map((item, index) => {
                                    if (index === 1) {
                                        return {...item, open: !item.open};
                                    }
                                    return item;
                                })
                            )
                        }
                        sx={{
                            backgroundColor: 'surface-container-high',
                            padding: '4px 4px 4px 16px',
                            height: '0',
                            minHeight: '0',
                            gap: '12px',
                            flexDirection: 'row-reverse',
                            alignItems: 'center',
                            '& .MuiAccordionSummary-content, .MuiAccordionSummary-content.Mui-expanded': {
                                alignItems: 'center',
                                margin: 0
                            },
                            '& .MuiAccordionSummary-root.Mui-expanded': {}
                        }}>
                        <Typography variant="title" size="medium" width="100%">
                            {userSetFEAnalysisConfigurations[1].category}
                        </Typography>
                        <Button
                            startIcon={<EditOutlinedIcon />}
                            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                e.stopPropagation();
                                handleNext(2);
                            }}>
                            Edit
                        </Button>
                    </AccordionSummary>
                    <Box px={5} paddingTop={3} pb={3}>
                        {userSetFEAnalysisConfigurations[1].parameters.map((param: any) => {
                            return (
                                <div>
                                    {param.values.map((item: any) => {
                                        return (
                                            <Box display="flex" gap={10} mb={1}>
                                                <Typography variant="title" size="medium" fontWeight={600} width={200}>
                                                    {item.name}
                                                </Typography>
                                                <Typography variant="body" size="large" width={350}>
                                                    {item.value}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </Box>
                </Accordion>
            </Box>
        </Box>
    );
};

export default FEReview;
